import { SignatureFieldType } from '@/api';
import type {
  SignatureDraftField,
  SignatureReviewParty,
} from '@/types/contractManagement/signatureFlow';

/** Font sizes the signing wizard offers for a field's text, in pt. */
export const FIELD_FONT_SIZES_PT = [8, 9, 10, 11, 12, 14, 16] as const;

export const DEFAULT_FIELD_FONT_SIZE_PT = 11;

/** Field types the provider fills with text, and so carry a font size. */
const TEXT_STYLE_FIELD_TYPES = new Set<SignatureFieldType>([
  SignatureFieldType.MENTION,
  SignatureFieldType.TEXT,
  SignatureFieldType.SIGNATURE_DATE,
  SignatureFieldType.SIGNER_NAME,
  SignatureFieldType.SIGNER_EMAIL,
  SignatureFieldType.READ_ONLY_TEXT,
]);

export function isTextStyleField(type: SignatureFieldType): boolean {
  return TEXT_STYLE_FIELD_TYPES.has(type);
}

// ── How a field's text will sit in its box ──────────────────────────────────
//
// The provider sets a field's text in Inconsolata unless the field names a
// font (the backend's default; the wizard never sets one). It is monospaced —
// every glyph 0.5 em wide — so how much fits a box is arithmetic, not a guess.

/** Width of one Inconsolata glyph, as a share of the font size. */
const PROVIDER_GLYPH_WIDTH_EM = 0.5;
/** Line height the provider sets field text at, as a multiple of the font size. */
export const PROVIDER_LINE_HEIGHT = 1.5;
/**
 * The preview draws in the app's own mono (Geist Mono, 0.6 em a glyph) and
 * tightens it to Inconsolata's 0.5 em, so lines wrap on screen where they will
 * on the PDF. ponytail: a hand-copied metric; load Inconsolata itself if the
 * preview ever needs the exact glyph shapes.
 */
export const PREVIEW_LETTER_SPACING_EM = PROVIDER_GLYPH_WIDTH_EM - 0.6;

export interface FieldCapacity {
  charsPerLine: number;
  lines: number;
  /** Characters the box holds in all, its lines filled edge to edge. */
  total: number;
}

/** How many characters a box of this size holds at `sizePt`. */
export function fieldCapacity(widthPt: number, heightPt: number, sizePt: number): FieldCapacity {
  const charsPerLine = Math.max(0, Math.floor(widthPt / (sizePt * PROVIDER_GLYPH_WIDTH_EM)));
  // One line needs the glyph height; each further one a full line height.
  const lines =
    heightPt < sizePt ? 0 : Math.max(1, Math.floor(heightPt / (sizePt * PROVIDER_LINE_HEIGHT)));
  return { charsPerLine, lines, total: charsPerLine * lines };
}

/** Lines `text` wraps to at `charsPerLine`: by word, a word longer than a line broken anywhere. */
export function wrappedLineCount(text: string, charsPerLine: number): number {
  if (charsPerLine <= 0) return Number.POSITIVE_INFINITY;
  let lines = 1;
  let used = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const needed = used === 0 ? word.length : used + 1 + word.length;
    if (needed <= charsPerLine) {
      used = needed;
      continue;
    }
    if (used > 0) lines += 1;
    lines += Math.ceil(word.length / charsPerLine) - 1;
    used = word.length % charsPerLine || charsPerLine;
  }
  return lines;
}

export interface FieldFit {
  capacity: FieldCapacity;
  fits: boolean;
  /** The largest offered size `text` fits at, when it doesn't fit at the current one. */
  fittingSizePt: number | null;
}

export function fieldFit(
  text: string,
  sizePt: number,
  widthPt: number,
  heightPt: number
): FieldFit {
  const fitsAt = (size: number): boolean => {
    const capacity = fieldCapacity(widthPt, heightPt, size);
    return wrappedLineCount(text, capacity.charsPerLine) <= capacity.lines;
  };
  const fits = fitsAt(sizePt);
  return {
    capacity: fieldCapacity(widthPt, heightPt, sizePt),
    fits,
    fittingSizePt: fits ? null : ([...FIELD_FONT_SIZES_PT].reverse().find(fitsAt) ?? null),
  };
}

// ── What a field shows while it is being placed ─────────────────────────────

/** Answers that read like the real thing for the palette's preset questions. */
const PRESET_SAMPLES: Record<string, string> = {
  City: 'Berlin',
  Capacity: 'Managing Director',
};

export interface FieldPreviewText {
  text: string;
  /**
   * True when the text is printed as-is (a mention, a read-only text). False for a
   * sample of what the signer or the provider will fill in — drawn quieter.
   */
  fixed: boolean;
}

/** The first value with something in it once trimmed — blanks fall through like missing ones. */
function firstFilled(...values: (string | null | undefined)[]): string {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

/**
 * The text a field will carry on the PDF, as close as the wizard can know it: the
 * fixed text of a mention or read-only text, the signer's real name and email, today
 * for the signing date, and for a question a sample answer (`sample`, typed by the
 * author to try one, else its default value or a stock answer). Null for fields
 * without text (signature, checkbox).
 */
export function fieldPreviewText(
  field: SignatureDraftField,
  party: SignatureReviewParty | undefined,
  sample?: string
): FieldPreviewText | null {
  const signer = party?.signer;
  const first = firstFilled(signer?.first_name);
  const last = firstFilled(signer?.last_name);
  switch (field.type) {
    case SignatureFieldType.MENTION:
      return { text: firstFilled(field.mention, 'Mention'), fixed: true };
    case SignatureFieldType.READ_ONLY_TEXT:
      return { text: firstFilled(field.text, 'Text'), fixed: true };
    case SignatureFieldType.SIGNER_NAME: {
      const full = firstFilled([first, last].join(' '), party?.name, 'Jane Doe');
      const byFormat = { first_name: first, last_name: last, full_name: full };
      return { text: firstFilled(byFormat[field.name_format ?? 'full_name'], full), fixed: false };
    }
    case SignatureFieldType.SIGNER_EMAIL:
      return { text: firstFilled(signer?.email, 'jane.doe@example.com'), fixed: false };
    case SignatureFieldType.SIGNATURE_DATE:
      return { text: new Date().toLocaleDateString('en-GB'), fixed: false };
    case SignatureFieldType.TEXT: {
      const answer = firstFilled(
        sample,
        field.default_value,
        PRESET_SAMPLES[firstFilled(field.question)],
        'Sample answer'
      );
      // The signer can't type past the max length, so neither does the preview.
      return {
        text: field.max_length ? answer.slice(0, field.max_length) : answer,
        fixed: false,
      };
    }
    default:
      return null;
  }
}
